/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { predecessorValueFormatter } from '@gh/builder-shared';
import { ScheduleTaskType, ScheduleType } from '@gh/builder-shared-data';
import {
  cellValueFormatter,
  code3Formatter,
  createDefaultGridModel,
  dictionaryValueFormatter,
  ROW_DELETED_CLASS,
  ROW_DIRTY_CLASS,
  ROW_GROUP_CLASS,
} from '@gh/common-internal-shared';
import { DateTimeTypes, getRouteInitData, LocalizationBusinessService, NumberTypes } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildButtonColumn,
  buildDateTimeColumn,
  buildEditableCheckboxColumn,
  buildNumberColumn,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { coalesce, isNotNil, toBoolean } from '@gh/core-util';
import { ValueFormatterParams } from 'ag-grid-community';
import { getScheduleTaskId, isScheduleTaskStart, scheduleTaskFieldState } from '@gh/builder-shared-gantt-chart';
import { compact, isEmpty } from 'lodash';
import { ScheduleTask } from '../../../resources/schedule-details.model';

@Component({
  selector: 'gh-schedule-details-task-list',
  template: require('./schedule-details-task-list.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleDetailsTaskListComponent implements OnInit {
  @Input() type: ScheduleType;
  @Input() editable: boolean;
  @Input() tasks: ScheduleTask[];
  @Input() gDriveUrl: ID;
  @Input() showBaseDates: boolean;

  @Output() setField = new EventEmitter<{ task: ScheduleTask; field: string; value: any }>();
  // tslint:disable-next-line:no-output-named-after-standard-event
  @Output() open = new EventEmitter<ScheduleTask>();
  @Output() remove = new EventEmitter<ScheduleTask>();
  @Output() saveBaseDates = new EventEmitter<void>();
  @Output() showBaseDatesChange = new EventEmitter<boolean>();

  gridDefinition: GridDefinition;

  get isGDriveUrlVisible(): boolean {
    return this.type === ScheduleType.Construction;
  }

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private translations: TranslationService,
              private localizationBusinessService: LocalizationBusinessService) {
  }

  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();
  }

  canTaskBeOpened(task: ScheduleTask): boolean {
    return !isScheduleTaskStart(task);
  }

  private buildGridDefinition(): GridDefinition {
    const { taskTypeDict, taskGridLayout } = getRouteInitData(this.route);

    const model = createDefaultGridModel({
      editable: this.editable,
      getId: getScheduleTaskId,
      getCellDef: scheduleTaskFieldState,
      command: (task: ScheduleTask, field, value) => this.setField.emit({ task, field, value }),
    });
    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model,
      layout: taskGridLayout,
      columns: {
        taskNumber: {
          valueFormatter: code3Formatter,
        },
        type: {
          valueFormatter: dictionaryValueFormatter(taskTypeDict),
        },
        ref: {
          valueFormatter: ({ data: { costCenterCode, orderNumber } }: ValueFormatterParams) =>
            [costCenterCode, orderNumber].filter(isNotNil).join('/'),
        },
        predecessors: {
          valueFormatter: predecessorValueFormatter,
        },
        completionPct: buildNumberColumn(NumberTypes.Percents),
        donePct: {
          field: 'canChangeCompletionPct',
          ...buildButtonColumn({
            type: 'raised',
            onClick: ({ data }) => this.toggleCompletion(data),
            isClickable: ({ data }) =>
              this.editable
              && (
              data.canChangeCompletionPct
              || (data.type === ScheduleTaskType.CostCenter || data.type === ScheduleTaskType.Empty)),
            label: this.translations.get('lbl_to_do_done'),
          }),
        },
        startDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        endDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        baseStartDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        baseEndDate: buildDateTimeColumn(DateTimeTypes.DateTime),
        duration: {
          valueFormatter: cellValueFormatter<number>((duration) =>
            this.localizationBusinessService.formatHoursDuration(duration)),
        },
        isInSupervisorSchedule: buildEditableCheckboxColumn(model.getCellAccessor('isInSupervisorSchedule')),
        notes: {
          valueFormatter: ({ data: { notes, todo } }: { data: ScheduleTask }) => compact([
            // tslint:disable-next-line:no-non-null-assertion
            isEmpty(notes) ? void 0 : `${notes!.length}${this.translations.get('lbl_n')}`,
            // tslint:disable-next-line:no-non-null-assertion
            isEmpty(todo) ? void 0 : `${todo!.length}${this.translations.get('lbl_t')}`,
          ]).join(', '),
        },
        isExcludeFromGanttChart: buildEditableCheckboxColumn(model.getCellAccessor('isExcludeFromGanttChart')),
        isFromCriticalPath: buildEditableCheckboxColumn(model.getCellAccessor('isFromCriticalPath')),
      },
      options: {
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: {
          [ROW_GROUP_CLASS]: ({ data: { hasChildren } }: { data: ScheduleTask }) => hasChildren,
          [ROW_DIRTY_CLASS]: ({ data: { isDirty } }: { data: ScheduleTask }) => toBoolean(isDirty),
          [ROW_DELETED_CLASS]: ({ data: { isResourceInUse } }: { data: ScheduleTask }) => isResourceInUse,
        },
      },
    });
  }

  private toggleCompletion(task: ScheduleTask): void {
    const currentCompletionPct = coalesce(task.completionPct, 0);
    // tslint:disable-next-line:no-magic-numbers
    const nextCompletionPct = currentCompletionPct < 50 ? 50 : (currentCompletionPct < 100 ? 100 : 0);
    this.setField.emit({ task, field: 'completionPct', value: nextCompletionPct });
  }
}
